import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import DataTable, { TableColumn } from 'react-data-table-component'
import {
    Alert,
    Badge,
    Button,
    Card,
    Col,
    Dropdown,
    Form,
    InputGroup,
    Row,
    Spinner,
} from 'react-bootstrap'
import {
    FaBuilding,
    FaCalendarAlt,
    FaCheckCircle,
    FaEdit,
    FaEllipsisV,
    FaEnvelope,
    FaKey,
    FaLock,
    FaPlus,
    FaSearch,
    FaShieldAlt,
    FaTrash,
    FaUnlock,
    FaUserCheck,
    FaUsers,
} from 'react-icons/fa'
import useFetch from '../../_fetch'
import { useAuth } from '../../auth/auth-context'
import { DataTableStyleSheetManager } from '../dashboard/_table/data-table-style-sheet-manager'
import ActivateUserModal from './activate-user-modal'
import AdminAccountAccessModal from './admin-account-access-modal'
import AdminEmailAccessModal from './admin-email-access-modal'
import ConvertTrialModal from './convert-trial-modal'
import DeleteUserModal from './delete-user-modal'
import LockUnlockModal from './lock-unlock-modal'
import ResetPasswordModal from './reset-password-modal'
import { IAdminEmailAccessOverview, IUser } from './types'
import UserModal from './user-modal'
import './user-management.css'

const emptyAccessOverview: IAdminEmailAccessOverview = { accounts: [], emails: [] }

// Render outside the responsive table's overflow container. Forward Popper's
// ref/styles and keep Dropdown's context for focus, selection and dismissal.
const AccountActionsMenu = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement> & { show?: boolean; close?: () => void; align?: unknown }>(
    ({ show, close, align, ...props }, ref) => createPortal(<div {...props} ref={ref} />, document.body),
)

const isSameUser = (left: IUser, right: IUser) =>
    left.username.toLowerCase() === right.username.toLowerCase()

type AccountStatus = 'active' | 'trial' | 'expired' | 'unverified' | 'locked'

function getAccountStatus(user: IUser): AccountStatus {
    if ((user.loginAttempts || 0) > 5) return 'locked'
    if (user.isVerified === false) return 'unverified'
    if (user.expirationDate) {
        const expiration = new Date(user.expirationDate).getTime()
        if (Number.isFinite(expiration) && expiration < Date.now()) return 'expired'
        return 'trial'
    }
    return 'active'
}

function AccountStatusBadge({ account }: { account: IUser }) {
    const status = getAccountStatus(account)
    const variants: Record<AccountStatus, { label: string, bg: string, text?: string }> = {
        active: { label: 'Active', bg: 'success' },
        trial: { label: 'Trial', bg: 'info', text: 'dark' },
        expired: { label: 'Trial expired', bg: 'danger' },
        unverified: { label: 'Unverified', bg: 'warning', text: 'dark' },
        locked: { label: 'Locked', bg: 'danger' },
    }
    const variant = variants[status]

    return (
        <div className="py-2">
            <Badge bg={variant.bg} text={variant.text as any}>{variant.label}</Badge>
            {account.expirationDate && status !== 'expired' && (
                <div className="text-muted small mt-1">Until {new Date(account.expirationDate).toLocaleDateString()}</div>
            )}
        </div>
    )
}

const tableStyles = {
    headRow: {
        style: {
            backgroundColor: '#f8fafc',
            borderBottomColor: '#e4e9f0',
            minHeight: '46px',
        },
    },
    headCells: {
        style: {
            color: '#667085',
            fontSize: '0.72rem',
            fontWeight: 700,
            letterSpacing: '0.035em',
            textTransform: 'uppercase' as const,
        },
    },
    rows: {
        style: {
            borderBottomColor: '#edf0f4',
            minHeight: '72px',
        },
        highlightOnHoverStyle: {
            backgroundColor: '#f8fbfe',
            borderBottomColor: '#e4e9f0',
        },
    },
    cells: {
        style: {
            color: '#344054',
            fontSize: '0.84rem',
            paddingBottom: '0.55rem',
            paddingTop: '0.55rem',
        },
    },
    pagination: {
        style: {
            borderTopColor: '#e4e9f0',
        },
    },
}

const UserManagement: React.FC = () => {
    const { user } = useAuth()
    const { doFetch } = useFetch()
    const [users, setUsers] = useState<IUser[]>([])
    const [accessOverview, setAccessOverview] = useState<IAdminEmailAccessOverview>(emptyAccessOverview)
    const [loading, setLoading] = useState(true)
    const [refreshing, setRefreshing] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [accessError, setAccessError] = useState<string | null>(null)
    const [activeView, setActiveView] = useState<'accounts' | 'emails'>('accounts')
    const [searchTerm, setSearchTerm] = useState('')
    const [kindFilter, setKindFilter] = useState('all')
    const [statusFilter, setStatusFilter] = useState('all')
    const [modalVisible, setModalVisible] = useState(false)
    const [resetPasswordModalVisible, setResetPasswordModalVisible] = useState(false)
    const [lockUnlockModalVisible, setLockUnlockModalVisible] = useState(false)
    const [currentUser, setCurrentUser] = useState<IUser | null>(null)
    const [isLocked, setIsLocked] = useState(false)
    const [deleteUserModalVisible, setDeleteUserModalVisible] = useState(false)
    const [activateUserModalVisible, setActivateUserModalVisible] = useState(false)
    const [convertTrialModalVisible, setConvertTrialModalVisible] = useState(false)
    const [accessAccount, setAccessAccount] = useState<IUser | null>(null)
    const [accountAccessModalVisible, setAccountAccessModalVisible] = useState(false)
    const [selectedEmailAddress, setSelectedEmailAddress] = useState<string | null>(null)
    const [emailAccessModalVisible, setEmailAccessModalVisible] = useState(false)

    const loadAdminData = useCallback(async (showInitialLoader = false) => {
        if (showInitialLoader) setLoading(true)
        else setRefreshing(true)
        setError(null)
        setAccessError(null)

        try {
            const [usersResponse, accessResponse] = await Promise.all([
                doFetch('/user/get-users'),
                doFetch('/user/admin/email-access'),
            ])

            if (usersResponse.status !== 200) throw new Error(await usersResponse.text().catch(() => 'Failed to load accounts.'))
            const usersBody = await usersResponse.json()
            setUsers(Array.isArray(usersBody) ? usersBody : [])

            if (accessResponse.status === 200) {
                const accessBody = await accessResponse.json()
                setAccessOverview({
                    accounts: Array.isArray(accessBody?.accounts) ? accessBody.accounts : [],
                    emails: Array.isArray(accessBody?.emails) ? accessBody.emails : [],
                })
            } else {
                setAccessError(await accessResponse.text().catch(() => 'E-mail access could not be loaded.'))
            }
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to load users.')
        } finally {
            setLoading(false)
            setRefreshing(false)
        }
    }, [doFetch])

    useEffect(() => { void loadAdminData(true) }, [loadAdminData])

    const reverseSwitchAccessMap = useMemo(() => users.reduce<Record<string, string[]>>((acc, account) => {
        for (const targetUsername of account.switchAccessUsernames || []) {
            if (!acc[targetUsername]) acc[targetUsername] = []
            acc[targetUsername].push(account.username)
        }
        return acc
    }, {}), [users])

    const filteredUsers = useMemo(() => {
        const query = searchTerm.trim().toLowerCase()
        return users.filter((account) => {
            if (query && !account.username.toLowerCase().includes(query)) return false
            if (kindFilter !== 'all' && account.kind !== kindFilter) return false
            if (statusFilter !== 'all' && getAccountStatus(account) !== statusFilter) return false
            return true
        })
    }, [kindFilter, searchTerm, statusFilter, users])

    const filteredEmails = useMemo(() => {
        const query = searchTerm.trim().toLowerCase()
        if (!query) return accessOverview.emails
        return accessOverview.emails.filter((identity) => identity.email.includes(query)
            || identity.accounts.some((account) => account.username.includes(query)))
    }, [accessOverview.emails, searchTerm])

    const selectedEmailAccess = selectedEmailAddress
        ? accessOverview.emails.find((entry) => entry.email === selectedEmailAddress) || null
        : null

    const emailOnlyAccounts = users.filter((account) => !account.isAdmin && account.emailAccess?.emailOnly).length
    const trialAccounts = users.filter((account) => ['trial', 'expired'].includes(getAccountStatus(account))).length
    const activeEmailMemberships = accessOverview.accounts.reduce((total, account) =>
        total + account.members.filter((member) => member.status === 'active').length, 0)

    const openAccountAccess = (account: IUser) => {
        setAccessAccount(account)
        setAccountAccessModalVisible(true)
    }

    const openAccountAccessByUsername = (username: string) => {
        const account = users.find((candidate) => candidate.username === username)
        if (!account) return
        setEmailAccessModalVisible(false)
        openAccountAccess(account)
    }

    const openEmailAccess = (email: string) => {
        setSelectedEmailAddress(email)
        setEmailAccessModalVisible(true)
    }

    const handleEdit = (account: IUser) => {
        setCurrentUser(account)
        setModalVisible(true)
    }

    const handleCreateUser = () => {
        setCurrentUser(null)
        setModalVisible(true)
    }

    const handleLockUnlock = (account: IUser, unlock: boolean) => {
        setCurrentUser(account)
        setIsLocked(unlock)
        setLockUnlockModalVisible(true)
    }

    const handleResetPassword = (account: IUser) => {
        setCurrentUser(account)
        setResetPasswordModalVisible(true)
    }

    const handleDeleteUser = (account: IUser) => {
        setCurrentUser(account)
        setDeleteUserModalVisible(true)
    }

    const handleOpenActivateModal = (account: IUser) => {
        setCurrentUser(account)
        setActivateUserModalVisible(true)
    }

    const accountColumns: TableColumn<IUser>[] = [
        {
            name: 'Account',
            selector: (row) => row.username,
            sortable: true,
            grow: 2,
            cell: (row) => (
                <div className="user-management-account-cell">
                    <span className="user-management-account-avatar">{row.username.slice(0, 2).toUpperCase()}</span>
                    <span className="min-w-0">
                        <strong className="d-block text-truncate" title={row.username}>{row.username}</strong>
                        <small className="text-muted">Account ID {row.id}</small>
                    </span>
                </div>
            ),
        },
        {
            name: 'Type',
            selector: (row) => row.isAdmin ? 'system admin' : row.kind,
            sortable: true,
            cell: (row) => <span className="text-capitalize">{row.isAdmin ? 'System admin' : row.kind}</span>,
        },
        {
            name: 'Access',
            grow: 2,
            cell: (row) => {
                if (row.isAdmin) return <span className="user-management-muted-value"><FaShieldAlt /> Global administration</span>
                const active = row.emailAccess?.activeMembers || 0
                const pending = row.emailAccess?.pendingMembers || 0
                const outgoing = row.switchAccessUsernames?.length || 0
                const incoming = reverseSwitchAccessMap[row.username]?.length || 0
                return (
                    <div className="user-management-access-cell">
                        <div className="d-flex flex-wrap align-items-center gap-2">
                            <button type="button" className="user-management-inline-action" onClick={() => openAccountAccess(row)}>
                                <FaEnvelope aria-hidden="true" /> {active} active{pending ? ` · ${pending} pending` : ''}
                            </button>
                            {row.emailAccess?.emailOnly && <Badge bg="primary">E-mail only</Badge>}
                        </div>
                        <small className="text-muted">Account switching: {outgoing} out / {incoming} in</small>
                    </div>
                )
            },
        },
        {
            name: 'Features',
            grow: 2,
            cell: (row) => row.isAdmin ? <span className="text-muted">—</span> : (
                <div className="user-management-feature-list">
                    <span className={!row.feed?.disabled ? 'is-enabled' : ''}>Feed</span>
                    <span className={!row.marketing?.disabled ? 'is-enabled' : ''}>Marketing</span>
                    <span className={row.byClick?.enabled ? 'is-enabled' : ''}>ByeLink</span>
                    <span className={row.partnerships?.enabled ? 'is-enabled' : ''}>Partnerships</span>
                    <span className={row.growthOpportunities?.enabled ? 'is-enabled' : ''}>Audit</span>
                    <span className={row.api?.enabled ? 'is-enabled' : ''}>API{row.api?.suspended ? ' (locked)' : ''}</span>
                </div>
            ),
        },
        {
            name: 'Status',
            sortable: true,
            sortFunction: (left, right) => getAccountStatus(left).localeCompare(getAccountStatus(right)),
            cell: (row) => <AccountStatusBadge account={row} />,
        },
        {
            name: '',
            cell: (row) => (
                <Dropdown align="end">
                    <Dropdown.Toggle variant="light" size="sm" className="user-management-actions-toggle" aria-label={`Actions for ${row.username}`}>
                        <FaEllipsisV aria-hidden="true" />
                    </Dropdown.Toggle>
                    <Dropdown.Menu as={AccountActionsMenu} className="user-management-actions-menu" popperConfig={{ strategy: 'fixed' }}>
                        <Dropdown.Header>Account</Dropdown.Header>
                        <Dropdown.Item onClick={() => handleEdit(row)}><FaEdit /> Edit account</Dropdown.Item>
                        {!row.isAdmin && <Dropdown.Item onClick={() => openAccountAccess(row)}><FaEnvelope /> Manage e-mail access</Dropdown.Item>}
                        <Dropdown.Item onClick={() => handleOpenActivateModal(row)}><FaCalendarAlt /> Activation and trial</Dropdown.Item>
                        <Dropdown.Divider />
                        <Dropdown.Header>Security</Dropdown.Header>
                        <Dropdown.Item onClick={() => handleResetPassword(row)}><FaKey /> Reset account password</Dropdown.Item>
                        {(row.loginAttempts || 0) > 5
                            ? <Dropdown.Item onClick={() => handleLockUnlock(row, true)}><FaUnlock /> Unlock account</Dropdown.Item>
                            : <Dropdown.Item onClick={() => handleLockUnlock(row, false)}><FaLock /> Lock account</Dropdown.Item>}
                        {row.isVerified === true && !!row.expirationDate && row.username.includes('@') && !row.isAdmin && <>
                            <Dropdown.Divider />
                            <Dropdown.Item onClick={() => { setCurrentUser(row); setConvertTrialModalVisible(true) }}><FaBuilding /> Convert trial account</Dropdown.Item>
                        </>}
                        <Dropdown.Divider />
                        <Dropdown.Item className="text-danger" onClick={() => handleDeleteUser(row)}><FaTrash /> Delete account</Dropdown.Item>
                    </Dropdown.Menu>
                </Dropdown>
            ),
        },
    ]

    const emailColumns: TableColumn<IAdminEmailAccessOverview['emails'][number]>[] = [
        {
            name: 'E-mail identity',
            selector: (row) => row.email,
            sortable: true,
            grow: 2,
            cell: (row) => (
                <div className="user-management-account-cell">
                    <span className="user-management-email-avatar"><FaEnvelope aria-hidden="true" /></span>
                    <span className="min-w-0">
                        <strong className="d-block text-truncate" title={row.email}>{row.email}</strong>
                        <small className="text-muted">{row.hasIdentity ? row.isVerified ? 'Verified identity' : 'Unverified identity' : 'Pending invitation'}</small>
                    </span>
                </div>
            ),
        },
        {
            name: 'Accounts',
            grow: 3,
            cell: (row) => (
                <div className="user-management-email-accounts">
                    {row.accounts.length ? row.accounts.map((account) => (
                        <button
                            type="button"
                            key={account.username}
                            className={`user-management-account-chip ${account.status === 'pending' ? 'is-pending' : ''}`}
                            onClick={() => openAccountAccessByUsername(account.username)}
                            title={`${account.role}${account.status === 'pending' ? ' · pending' : ''}`}
                        >
                            {account.username}
                            <small>{account.role}{account.status === 'pending' ? ' · pending' : ''}</small>
                        </button>
                    )) : <span className="text-muted">No account access</span>}
                </div>
            ),
        },
        {
            name: 'Primary account',
            selector: (row) => row.primaryAccount || '',
            sortable: true,
            cell: (row) => row.primaryAccount
                ? <span className="user-management-primary-account"><FaCheckCircle aria-hidden="true" /> {row.primaryAccount}</span>
                : <span className="text-muted">—</span>,
        },
        {
            name: 'Status',
            selector: (row) => row.isVerified ? 'verified' : row.hasIdentity ? 'unverified' : 'invited',
            sortable: true,
            cell: (row) => (
                <Badge bg={row.isVerified ? 'success' : 'warning'} text={row.isVerified ? undefined : 'dark'}>
                    {row.isVerified ? 'Verified' : row.hasIdentity ? 'Unverified' : 'Invitation only'}
                </Badge>
            ),
        },
        {
            name: '',
            cell: (row) => <Button size="sm" variant="outline-primary" onClick={() => openEmailAccess(row.email)}>Manage</Button>,
        },
    ]

    const handleActivateUserSubmit = async (activationType: 'full' | 'date', expirationDate?: string) => {
        if (!currentUser) return
        const response = await doFetch('/user/activate', { username: currentUser.username, activationType, expirationDate })
        if (response.status !== 200) throw new Error(await response.text().catch(() => 'Failed to activate user.'))
        const updatedUser: IUser = await response.json()
        setUsers((current) => current.map((account) => isSameUser(account, currentUser) ? { ...account, ...updatedUser } : account))
        setActivateUserModalVisible(false)
        setCurrentUser(null)
    }

    const handleDeleteUserSubmit = async () => {
        if (!currentUser) return
        const response = await doFetch('/remove-user-full', { username: currentUser.username })
        if (response.status !== 200) throw new Error(await response.text())
        setUsers((current) => current.filter((account) => !isSameUser(account, currentUser)))
        setDeleteUserModalVisible(false)
        setCurrentUser(null)
        void loadAdminData()
    }

    const handleLockUnlockSubmit = async (unlock: boolean) => {
        if (currentUser) {
            const response = await doFetch('/user/account-lock', { username: currentUser.username, kind: unlock ? 'unlock' : 'lock' })
            if (response.status !== 200) throw new Error(`Something went wrong: ${await response.text()}`)
            setUsers((current) => current.map((account) => isSameUser(account, currentUser)
                ? { ...account, loginAttempts: unlock ? 0 : 10 }
                : account))
        }
        setLockUnlockModalVisible(false)
    }

    const handlePasswordSubmit = async (newPassword: string) => {
        if (currentUser) {
            const response = await doFetch('/user/reset-password', { username: currentUser.username, password: newPassword })
            if (response.status !== 200) throw new Error(`Something went wrong: ${await response.text()}`)
        }
        setResetPasswordModalVisible(false)
    }

    if (!user?.isAdmin) return null

    return (
        <div className="container-fluid px-2 px-md-3 user-management-page">
            <Card className="user-management-hero border-0 mb-4">
                <Card.Body className="d-flex flex-column flex-lg-row justify-content-between gap-3 align-items-lg-center">
                    <div>
                        <div className="user-management-eyebrow">Administration</div>
                        <h4 className="mb-1">User management</h4>
                        <div className="text-muted">Manage customer accounts, e-mail identities, product access and account security.</div>
                    </div>
                    <Button onClick={handleCreateUser}><FaPlus className="me-2" aria-hidden="true" />Create account</Button>
                </Card.Body>
            </Card>

            <Row className="g-3 mb-4">
                <Col xs={6} xl={3}>
                    <Card className="user-management-stat-card border-0 h-100"><Card.Body>
                        <span className="user-management-stat-icon"><FaBuilding /></span>
                        <span><strong>{users.filter((account) => !account.isAdmin).length}</strong><small>Customer accounts</small></span>
                    </Card.Body></Card>
                </Col>
                <Col xs={6} xl={3}>
                    <Card className="user-management-stat-card border-0 h-100"><Card.Body>
                        <span className="user-management-stat-icon"><FaUsers /></span>
                        <span><strong>{accessOverview.emails.length}</strong><small>E-mail identities</small></span>
                    </Card.Body></Card>
                </Col>
                <Col xs={6} xl={3}>
                    <Card className="user-management-stat-card border-0 h-100"><Card.Body>
                        <span className="user-management-stat-icon"><FaUserCheck /></span>
                        <span><strong>{activeEmailMemberships}</strong><small>Active memberships</small></span>
                    </Card.Body></Card>
                </Col>
                <Col xs={6} xl={3}>
                    <Card className="user-management-stat-card border-0 h-100"><Card.Body>
                        <span className="user-management-stat-icon"><FaShieldAlt /></span>
                        <span><strong>{emailOnlyAccounts}</strong><small>E-mail-only · {trialAccounts} trials</small></span>
                    </Card.Body></Card>
                </Col>
            </Row>

            {error && <Alert variant="danger" className="d-flex justify-content-between align-items-center gap-3">
                <span>{error}</span><Button size="sm" variant="outline-danger" onClick={() => void loadAdminData(true)}>Try again</Button>
            </Alert>}

            <Card className="user-management-panel border-0">
                <Card.Header className="bg-white border-0 user-management-panel-header">
                    <div className="user-management-view-tabs" role="tablist" aria-label="User management views">
                        <button type="button" role="tab" aria-selected={activeView === 'accounts'} className={activeView === 'accounts' ? 'is-active' : ''} onClick={() => { setActiveView('accounts'); setSearchTerm('') }}>
                            <FaBuilding aria-hidden="true" /> Accounts <span>{users.length}</span>
                        </button>
                        <button type="button" role="tab" aria-selected={activeView === 'emails'} className={activeView === 'emails' ? 'is-active' : ''} onClick={() => { setActiveView('emails'); setSearchTerm('') }}>
                            <FaEnvelope aria-hidden="true" /> E-mail access <span>{accessOverview.emails.length}</span>
                        </button>
                    </div>
                </Card.Header>

                <div className="user-management-toolbar">
                    <InputGroup className="user-management-search">
                        <InputGroup.Text><FaSearch aria-hidden="true" /></InputGroup.Text>
                        <Form.Control
                            type="search"
                            aria-label={activeView === 'accounts' ? 'Search accounts' : 'Search e-mail access'}
                            placeholder={activeView === 'accounts' ? 'Search by username' : 'Search by e-mail or account'}
                            value={searchTerm}
                            onChange={(event) => setSearchTerm(event.target.value)}
                        />
                    </InputGroup>
                    {activeView === 'accounts' && <>
                        <Form.Select aria-label="Filter by account type" value={kindFilter} onChange={(event) => setKindFilter(event.target.value)}>
                            <option value="all">All account types</option>
                            <option value="publisher">Publishers</option>
                            <option value="advertiser">Advertisers</option>
                        </Form.Select>
                        <Form.Select aria-label="Filter by account status" value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
                            <option value="all">All statuses</option>
                            <option value="active">Active</option>
                            <option value="trial">Trial</option>
                            <option value="expired">Trial expired</option>
                            <option value="unverified">Unverified</option>
                            <option value="locked">Locked</option>
                        </Form.Select>
                    </>}
                    {refreshing && <div className="user-management-refreshing"><Spinner animation="border" size="sm" /> Updating…</div>}
                </div>

                {activeView === 'emails' && accessError && <Alert variant="warning" className="mx-3 mt-3 mb-0">{accessError}</Alert>}

                <div role="tabpanel">
                    {activeView === 'accounts' ? (
                        <DataTableStyleSheetManager>
                            <DataTable
                                key="accounts"
                                columns={accountColumns}
                                data={filteredUsers}
                                keyField="username"
                                pagination
                                paginationPerPage={20}
                                paginationRowsPerPageOptions={[20, 50, 100]}
                                paginationComponentOptions={{ rowsPerPageText: 'Rows per page', rangeSeparatorText: 'of' }}
                                defaultSortFieldId={1}
                                striped={false}
                                highlightOnHover
                                persistTableHead
                                responsive
                                noHeader
                                progressPending={loading}
                                progressComponent={<div className="user-management-loading"><Spinner animation="border" /><span>Loading accounts…</span></div>}
                                noDataComponent={<div className="user-management-empty">No accounts match the selected filters.</div>}
                                customStyles={tableStyles}
                            />
                        </DataTableStyleSheetManager>
                    ) : (
                        <DataTableStyleSheetManager>
                            <DataTable
                                key="emails"
                                columns={emailColumns}
                                data={filteredEmails}
                                keyField="email"
                                pagination
                                paginationPerPage={20}
                                paginationRowsPerPageOptions={[20, 50, 100]}
                                paginationComponentOptions={{ rowsPerPageText: 'Rows per page', rangeSeparatorText: 'of' }}
                                defaultSortFieldId={1}
                                striped={false}
                                highlightOnHover
                                persistTableHead
                                responsive
                                noHeader
                                progressPending={loading}
                                progressComponent={<div className="user-management-loading"><Spinner animation="border" /><span>Loading e-mail access…</span></div>}
                                noDataComponent={<div className="user-management-empty">No e-mail identities match your search.</div>}
                                customStyles={tableStyles}
                            />
                        </DataTableStyleSheetManager>
                    )}
                </div>
            </Card>

            <UserModal
                show={modalVisible}
                onHide={() => { setModalVisible(false); setCurrentUser(null) }}
                currentUser={currentUser}
                availableUsers={users}
                onSubmit={async (account, logo) => {
                    const response = await doFetch(currentUser ? '/user/update' : '/user/create', (() => {
                        if (currentUser) (account as any).id = currentUser.id
                        const formData = new FormData()
                        formData.append('logo', logo || '')
                        formData.append('user', JSON.stringify(account))
                        return formData
                    })())
                    if (response.status !== 200) throw new Error(await response.text())
                    const updatedUser: IUser = await response.json()
                    if (currentUser) {
                        setUsers((current) => current.map((item) => isSameUser(item, currentUser) ? { ...item, ...updatedUser } : item))
                    } else {
                        setUsers((current) => [...current, updatedUser])
                    }
                }}
            />

            <AdminAccountAccessModal
                show={accountAccessModalVisible}
                account={accessAccount}
                onHide={() => { setAccountAccessModalVisible(false); setAccessAccount(null) }}
                onChanged={() => { void loadAdminData() }}
            />

            <AdminEmailAccessModal
                show={emailAccessModalVisible}
                emailAccess={selectedEmailAccess}
                accounts={users}
                onHide={() => { setEmailAccessModalVisible(false); setSelectedEmailAddress(null) }}
                onChanged={() => loadAdminData()}
                onManageAccount={openAccountAccessByUsername}
            />

            <ConvertTrialModal
                show={convertTrialModalVisible}
                user={currentUser}
                onHide={() => { setConvertTrialModalVisible(false); setCurrentUser(null) }}
                onConverted={(previous, converted) => {
                    setUsers((current) => current.map((item) => isSameUser(item, previous)
                        ? { ...item, ...converted, expirationDate: undefined, emailAccess: { emailOnly: true, activeManagers: 1, activeMembers: 1, pendingMembers: 0 } }
                        : item))
                    void loadAdminData()
                }}
            />

            <ResetPasswordModal show={resetPasswordModalVisible} onHide={() => setResetPasswordModalVisible(false)} onSubmit={handlePasswordSubmit} />
            <LockUnlockModal show={lockUnlockModalVisible} onHide={() => setLockUnlockModalVisible(false)} onSubmit={handleLockUnlockSubmit} isLocked={isLocked} />
            <DeleteUserModal show={deleteUserModalVisible} onHide={() => setDeleteUserModalVisible(false)} onSubmit={handleDeleteUserSubmit} username={currentUser?.username} />
            <ActivateUserModal
                show={activateUserModalVisible}
                onHide={() => { setActivateUserModalVisible(false); setCurrentUser(null) }}
                username={currentUser?.username}
                hasTrialDate={!!currentUser?.expirationDate}
                onSubmit={handleActivateUserSubmit}
            />
        </div>
    )
}

export default UserManagement
